/**
 * Die Lieferantenseite — ein Blatt von rechts, am Telefon Vollbild.
 *
 * Benjamin, 25.09.2026: „Jeder Lieferant hat eine intern pflegbare Liste von
 * Materialien. Das ist Teil der Lieferantenseite." Der Stern macht ihn zum
 * Standardlieferanten eines Materials; im Projekt gilt dann er, bis man dort
 * einen anderen wählt.
 *
 * Dazu Adresse, Ansprechpartner (anrufen, schreiben), Notizen, Aufgaben und
 * die Projekte, in denen er vorkommt.
 */
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import { fehlerText } from "@vimaana/ui";

import {
  ANFRAGE_ROLLE, istAnfrageRolle, useLieferantAendern, useLieferantBlatt, useLieferantKontakt, useLieferantMaterial, useMaterialStammlisten,
  type LieferantBlattDaten, type LieferantKontakt, type LieferantMaterial, type LieferantRechnung, type RechnungStand,
} from "../../hooks/useLieferanten";
import { telLink } from "../../lib/notizen";
import { useNachrichtenFenster } from "../nachrichten/NachrichtenKontext";
import NotizenBlock from "../notizen/NotizenBlock";
import StrassenFeld from "../kunden/StrassenFeld";
import PostSchreiben, { type SchreibZiel } from "../projekt/PostSchreiben";
import MaterialWahl from "./MaterialWahl";
import { ArtikelPruefen } from "./ArtikelPruefen";
import { useArtikelOffen } from "../../hooks/useArtikel";
import "../../styles/kunden.css";
import "../../styles/lieferanten.css";

const eur = (n: number | null) => (n == null ? "" : n.toLocaleString("de-DE", { style: "currency", currency: "EUR" }));

export default function LieferantBlatt({ lieferantId, onClose }: { lieferantId: string; onClose: () => void }) {
  const blatt = useLieferantBlatt(lieferantId);
  useEffect(() => {
    const zu = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", zu, true);
    return () => window.removeEventListener("keydown", zu, true);
  }, [onClose]);
  const d = blatt.data;
  return createPortal(
    <div className="k-blatt lf-blatt" role="dialog" aria-modal="true" aria-label={d ? `Lieferant ${d.name}` : "Lieferant"}>
      <button type="button" className="k-blatt-schleier" aria-label="Schließen" onClick={onClose} />
      <aside className="k-blatt-panel">
        <header className="k-blatt-kopf">
          <h2>{d?.name || "Lieferant"}</h2>
          <button type="button" className="k-blatt-zu" aria-label="Schließen" onClick={onClose}>×</button>
        </header>
        <div className="k-blatt-inhalt">
          {blatt.isPending && <p className="lf-leer">Lade …</p>}
          {blatt.isError && <p className="lf-fehler">{fehlerText(blatt.error)}</p>}
          {d && <BlattInhalt d={d} onClose={onClose} />}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

/**
 * Dasselbe ohne Blatt — für die rechte Spalte der Seite „Lieferanten".
 * `onMaterial` macht Materialnamen zu Sprüngen in die Materialansicht.
 */
export function LieferantDetail({ lieferantId, onClose, onMaterial }: { lieferantId: string; onClose?: () => void; onMaterial?: (id: string) => void }) {
  const blatt = useLieferantBlatt(lieferantId);
  const d = blatt.data;
  if (blatt.isPending) return <p className="lf-leer">Lade …</p>;
  if (blatt.isError || !d) return <p className="lf-fehler">{fehlerText(blatt.error)}</p>;
  return (
    <div className="lf-detail">
      <header className="lf-detail-kopf">
        <div>
          <p className="lf-detail-ueber">Lieferant</p>
          <h2>{d.name}</h2>
        </div>
        {onClose && <button type="button" className="lf-knopf lf-detail-zu" onClick={onClose} aria-label="Schließen">×</button>}
      </header>
      <BlattInhalt d={d} onClose={onClose ?? (() => {})} onMaterial={onMaterial} />
    </div>
  );
}

function Abschnitt({ titel, rechts, children }: { titel: string; rechts?: ReactNode; children: ReactNode }) {
  return (
    <section className="lf-abschnitt">
      <div className="lf-abschnitt-kopf"><h3>{titel}</h3>{rechts}</div>
      {children}
    </section>
  );
}

function BlattInhalt({ d, onClose, onMaterial }: { d: LieferantBlattDaten; onClose: () => void; onMaterial?: (id: string) => void }) {
  const nachrichten = useNachrichtenFenster();
  const [schreiben, setSchreiben] = useState<string | null>(null);
  const ziele: SchreibZiel[] = d.kontakte.filter((k) => k.email).map((k) => ({ email: k.email, wer: [k.name, k.rolle].filter(Boolean).join(" · ") || d.name }));
  const telefon = d.kontakte.find((k) => k.telefon)?.telefon || "";
  return (
    <>
      <div className="k-schnell lf-schnell">
        {telefon && <a className="vg-chip-knopf" href={telLink(telefon)}>✆ Anrufen</a>}
        {ziele.length > 0 && <button type="button" className="vg-chip-knopf" onClick={() => setSchreiben(ziele[0].email)}>✎ Mail</button>}
        <button type="button" className="vg-chip-knopf" onClick={() => { onClose(); nachrichten.oeffnen({ schluessel: `lieferant:${d.id}` }); }}>Nachrichten</button>
      </div>

      <MaterialAbschnitt d={d} onMaterial={onMaterial} />
      <StammAbschnitt d={d} />
      <KontakteAbschnitt d={d} onSchreiben={setSchreiben} />
      <RechnungenAbschnitt d={d} />

      <Abschnitt titel="Projekte">
        {d.projekte.length === 0 ? (
          <p className="lf-leer">Noch in keinem Projekt — über Material am Projekt oder „+ Lieferant“ dort.</p>
        ) : (
          <ul className="lf-liste">
            {d.projekte.map((p) => (
              <li key={p.id} className={"lf-zeile" + (p.laufend ? "" : " ist-alt")}>
                <Link to={`/projekte/${p.id}`} className="lf-zeile-haupt">
                  <span className="lf-zeile-name">{p.title || "Projekt"}</span>
                  <span className="lf-zeile-info">{[p.kunde_name, p.status, p.materialien ? `${p.materialien} Material` : ""].filter(Boolean).join(" · ")}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Abschnitt>

      <NotizenBlock bezug={{ lieferant_firma_id: d.id }} titel="Aufgaben & Notizen" platzhalter="Was ist zu tun? z. B. „Preisliste anfragen“" />

      {schreiben !== null && createPortal(
        <PostSchreiben onClose={() => setSchreiben(null)} lieferantId={d.id} ziele={ziele} an={schreiben} titel={d.name} />,
        document.body,
      )}
    </>
  );
}

// ── Stamm: Adresse und Notizen ───────────────────────────────────────────

function teileAdresse(a: string) {
  const teile = (a || "").split(",").map((t) => t.trim()).filter(Boolean);
  const mitPlz = teile.findIndex((t) => /^\d{5}\s/.test(t));
  if (mitPlz > 0) {
    const [, plz, ort] = /^(\d{5})\s+(.*)$/.exec(teile.slice(mitPlz).join(", ")) || [];
    return { strasse: teile.slice(0, mitPlz).join(", "), plz: plz || "", ort: ort || "" };
  }
  return { strasse: teile.join(", "), plz: "", ort: "" };
}

function StammAbschnitt({ d }: { d: LieferantBlattDaten }) {
  const aendern = useLieferantAendern(d.id);
  const [offen, setOffen] = useState(false);
  const start = teileAdresse(d.adresse);
  const [strasse, setStrasse] = useState(start.strasse);
  const [plz, setPlz] = useState(start.plz);
  const [ort, setOrt] = useState(start.ort);
  const [name, setName] = useState(d.name);
  const [notizen, setNotizen] = useState(d.notizen);
  useEffect(() => { setNotizen(d.notizen); }, [d.notizen]);
  const [kundennummer, setKundennummer] = useState(d.kundennummer);
  useEffect(() => { setKundennummer(d.kundennummer); }, [d.kundennummer]);
  const zeile = [strasse.trim(), [plz.trim(), ort.trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return (
    <Abschnitt titel="Stammdaten" rechts={!offen && <button type="button" className="lf-knopf" onClick={() => setOffen(true)}>Bearbeiten</button>}>
      <GruppeWahl d={d} />
      {!offen ? (
        <div className="lf-stamm">
          <div><span className="lf-label-klein">Adresse</span>{d.adresse || <span className="lf-leer">Keine Adresse hinterlegt</span>}</div>
        </div>
      ) : (
        <div className="lf-form">
          <label className="lf-label">Name<input className="lf-feld" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="lf-label">Straße
            <StrassenFeld value={strasse} onChange={setStrasse} inputClassName="lf-feld"
              onPick={(t) => { setStrasse(t.strasse); if (t.plz) setPlz(t.plz); if (t.ort) setOrt(t.ort); }} />
          </label>
          <div className="lf-form-zwei is-plz">
            <label className="lf-label">PLZ<input className="lf-feld" value={plz} inputMode="numeric" onChange={(e) => setPlz(e.target.value)} /></label>
            <label className="lf-label">Ort<input className="lf-feld" value={ort} onChange={(e) => setOrt(e.target.value)} /></label>
          </div>
          {aendern.isError && <div className="lf-fehler">{fehlerText(aendern.error)}</div>}
          <div className="lf-knoepfe">
            <button type="button" className="lf-knopf" onClick={() => setOffen(false)}>Abbrechen</button>
            <button type="button" className="lf-knopf is-primaer" disabled={!name.trim() || aendern.isPending}
              onClick={() => aendern.mutate({ name: name.trim(), adresse: zeile }, { onSuccess: () => setOffen(false) })}>
              {aendern.isPending ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </div>
      )}
      <label className="lf-label lf-kundennummer">Unsere Kundennummer
        <input className="lf-feld" value={kundennummer} onChange={(e) => setKundennummer(e.target.value)}
          placeholder="steht dann in jeder Anfrage"
          onBlur={() => { if (kundennummer.trim() !== d.kundennummer) aendern.mutate({ kundennummer: kundennummer.trim() }); }}
          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
      </label>
      <label className="lf-label lf-notizen">Interne Notizen
        <textarea className="lf-feld" rows={2} value={notizen} onChange={(e) => setNotizen(e.target.value)}
          placeholder="Öffnungszeiten, Lieferbedingungen …"
          onBlur={() => { if (notizen !== d.notizen) aendern.mutate({ notizen }); }} />
      </label>
    </Abschnitt>
  );
}

/** Warengruppen: Chips zum Antippen, mehrere möglich (Migration 218), der Vorschlag hervorgehoben. */
function GruppeWahl({ d }: { d: LieferantBlattDaten }) {
  const listen = useMaterialStammlisten();
  const aendern = useLieferantAendern(d.id);
  const gruppen = listen.data?.gruppen ?? [];
  return (
    <div className="lf-gruppenwahl">
      <span className="lf-label-klein">Warengruppen</span>
      <div className="lf-gruppenchips" role="group" aria-label="Warengruppen">
        {gruppen.map((g) => {
          const gesetzt = d.gruppen || [];
          const an = gesetzt.includes(g.slug);
          const vorschlag = !gesetzt.length && d.gruppe_vorschlag === g.slug;
          return (
            <button key={g.slug} type="button" aria-pressed={an} disabled={aendern.isPending}
              className={"vg-chip-knopf" + (an ? " is-active" : "") + (vorschlag ? " ist-vorschlag" : "")}
              title={vorschlag ? "Vorschlag — antippen zum Übernehmen" : undefined}
              onClick={() => aendern.mutate({ gruppen: an ? gesetzt.filter((x) => x !== g.slug) : [...gesetzt, g.slug] })}>
              {g.label}{vorschlag ? " ?" : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Rechnungen ───────────────────────────────────────────────────────────

const STAND_TEXT: Record<RechnungStand, string> = {
  erfassung: "in Erfassung", offen: "offen", im_korb: "im Ausgangskorb", angewiesen: "angewiesen", bezahlt: "bezahlt",
};
const tagKurz = (d: string) => (/^\d{4}-\d{2}-\d{2}/.test(d) ? new Date(d.slice(0, 10)).toLocaleDateString("de-DE") : d);

/**
 * Die Rechnungen des Lieferanten mit ihrem Stand (Benjamin, 25.09.2026: „An den
 * Lieferanten hängen Rechnungsbelege, die wir verarbeiten — ich möchte sie
 * sehen, mit Status offen oder bezahlt"). Offen zuerst; ein Tipp öffnet den
 * Beleg im Belege-Reiter. Bezahlt heißt dasselbe wie im Ausgangskorb: eine
 * Bankzeile, „bezahlt", bar, privat oder ausgeführte Überweisung.
 */
function RechnungenAbschnitt({ d }: { d: LieferantBlattDaten }) {
  const r = d.rechnungen;
  const [alle, setAlle] = useState(false);
  if (!r || r.anzahl === 0) {
    return <Abschnitt titel="Rechnungen"><p className="lf-leer">Noch keine Belege von diesem Lieferanten.</p></Abschnitt>;
  }
  const offen = (z: LieferantRechnung) => z.stand === "offen" || z.stand === "im_korb" || z.stand === "angewiesen" || z.stand === "erfassung";
  const liste = alle ? r.liste : r.liste.filter(offen);
  return (
    <Abschnitt titel={`Rechnungen (${r.anzahl})`} rechts={
      <div className="lf-rechnung-filter" role="group" aria-label="Rechnungen filtern">
        <button type="button" className={"vg-chip-knopf" + (!alle ? " is-active" : "")} aria-pressed={!alle} onClick={() => setAlle(false)}>Offen</button>
        <button type="button" className={"vg-chip-knopf" + (alle ? " is-active" : "")} aria-pressed={alle} onClick={() => setAlle(true)}>Alle</button>
      </div>
    }>
      <p className="lf-hinweis">
        {r.offen_anzahl > 0
          ? <>Offen: <strong>{r.offen_anzahl}</strong> · {eur(r.offen_summe)}</>
          : "Alles bezahlt."}
      </p>
      {liste.length === 0 && <p className="lf-leer">Keine offene Rechnung.</p>}
      <ul className="lf-liste">
        {liste.map((z) => (
          <li key={z.id} className="lf-zeile lf-rzeile">
            <Link to={`/finanzen?reiter=belege&beleg=${encodeURIComponent(z.id)}`} className="lf-zeile-haupt" title="Beleg öffnen">
              <span className="lf-zeile-name">{z.rechnungsnummer ? `Nr. ${z.rechnungsnummer}` : "ohne Nummer"} · {tagKurz(z.datum) || "ohne Datum"}</span>
              <span className="lf-zeile-info">
                {[z.beschreibung, z.projekt_title,
                  z.stand === "bezahlt" && z.bezahlt_am ? `bezahlt am ${tagKurz(z.bezahlt_am)}` : "",
                  z.faelligkeit && z.stand !== "bezahlt" ? `fällig ${tagKurz(z.faelligkeit)}` : ""].filter(Boolean).join(" · ")}
              </span>
            </Link>
            <div className="lf-rzeile-rechts">
              <span className="lf-rzeile-betrag">{eur(z.brutto)}</span>
              <span className={"lf-rstand ist-" + (z.ueberfaellig ? "ueberfaellig" : z.stand)}>{z.ueberfaellig ? "überfällig" : STAND_TEXT[z.stand]}</span>
            </div>
          </li>
        ))}
      </ul>
    </Abschnitt>
  );
}

// ── Ansprechpartner ──────────────────────────────────────────────────────

function KontakteAbschnitt({ d, onSchreiben }: { d: LieferantBlattDaten; onSchreiben: (email: string) => void }) {
  const [neu, setNeu] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<string | null>(null);
  return (
    <Abschnitt titel="Ansprechpartner" rechts={!neu && <button type="button" className="lf-knopf" onClick={() => setNeu(true)}>＋ Ansprechpartner</button>}>
      {d.kontakte.length === 0 && !neu && <p className="lf-leer">Noch niemand hinterlegt.</p>}
      {d.kontakte.length > 0 && !d.kontakte.some((k) => k.email && istAnfrageRolle(k.rolle)) && (
        <p className="lf-hinweis">
          Anfragen aus Projekten gehen an den Ansprechpartner mit der Rolle „{ANFRAGE_ROLLE}“ — sonst an eine Adresse,
          die nicht nach Rechnungsversand aussieht.
        </p>
      )}
      <ul className="lf-liste">
        {d.kontakte.map((k) => bearbeitet === k.id ? (
          <li key={k.id}><KontaktForm lieferantId={d.id} kontakt={k} onFertig={() => setBearbeitet(null)} /></li>
        ) : (
          <li key={k.id} className="lf-zeile">
            <div className="lf-zeile-haupt">
              <span className="lf-zeile-name">{k.name || k.email || k.telefon}{k.email && istAnfrageRolle(k.rolle) && <em className="lf-art">Anfragen</em>}</span>
              <span className="lf-zeile-info">{[k.rolle, k.email, k.telefon].filter(Boolean).join(" · ")}</span>
            </div>
            <div className="lf-zeile-tat">
              {k.telefon && <a className="lf-knopf" href={telLink(k.telefon)} title={`${k.telefon} anrufen`}>✆</a>}
              {k.email && <button type="button" className="lf-knopf" title={`Mail an ${k.email}`} onClick={() => onSchreiben(k.email)}>✎</button>}
              <button type="button" className="lf-knopf" onClick={() => setBearbeitet(k.id)}>Ändern</button>
            </div>
          </li>
        ))}
      </ul>
      {neu && <KontaktForm lieferantId={d.id} onFertig={() => setNeu(false)} />}
    </Abschnitt>
  );
}

function KontaktForm({ lieferantId, kontakt, onFertig }: { lieferantId: string; kontakt?: LieferantKontakt; onFertig: () => void }) {
  const { anlegen, aendern, entfernen } = useLieferantKontakt(lieferantId);
  const [w, setW] = useState({ name: kontakt?.name || "", rolle: kontakt?.rolle || "", email: kontakt?.email || "", telefon: kontakt?.telefon || "" });
  const [loeschen, setLoeschen] = useState(false);
  const feld = (k: keyof typeof w, label: string, typ = "text") => (
    <label className="lf-label">{label}
      <input className="lf-feld" type={typ} value={w[k]} onChange={(e) => setW((v) => ({ ...v, [k]: e.target.value }))} />
    </label>
  );
  const fehler = anlegen.error || aendern.error || entfernen.error;
  const laeuft = anlegen.isPending || aendern.isPending || entfernen.isPending;
  return (
    <div className="lf-form">
      <div className="lf-form-zwei">{feld("name", "Name")}{feld("rolle", "Rolle")}</div>
      <div className="lf-form-zwei">{feld("email", "Mail", "email")}{feld("telefon", "Telefon", "tel")}</div>
      <label className="lf-merken">
        <input type="checkbox" checked={istAnfrageRolle(w.rolle)}
          onChange={(e) => setW((v) => ({ ...v, rolle: e.target.checked ? ANFRAGE_ROLLE : (istAnfrageRolle(kontakt?.rolle || "") ? "" : kontakt?.rolle || "") }))} />
        <span>Bekommt Anfragen aus Projekten</span>
      </label>
      {fehler && <div className="lf-fehler">{fehlerText(fehler)}</div>}
      <div className="lf-knoepfe">
        {kontakt && (loeschen ? (
          <button type="button" className="lf-knopf is-gefahr" disabled={laeuft} onClick={() => entfernen.mutate(kontakt.id, { onSuccess: onFertig })}>Wirklich entfernen</button>
        ) : (
          <button type="button" className="lf-knopf is-gefahr" onClick={() => setLoeschen(true)}>Entfernen</button>
        ))}
        <span className="lf-luecke" />
        <button type="button" className="lf-knopf" onClick={onFertig}>Abbrechen</button>
        <button type="button" className="lf-knopf is-primaer" disabled={laeuft || !(w.name.trim() || w.email.trim() || w.telefon.trim())}
          onClick={() => (kontakt
            ? aendern.mutate({ kontaktId: kontakt.id, werte: w }, { onSuccess: onFertig })
            : anlegen.mutate(w, { onSuccess: onFertig }))}>
          {laeuft ? "Speichert …" : "Speichern"}
        </button>
      </div>
    </div>
  );
}

// ── Materialliste ────────────────────────────────────────────────────────

function MaterialAbschnitt({ d, onMaterial }: { d: LieferantBlattDaten; onMaterial?: (id: string) => void }) {
  const m = useLieferantMaterial(d.id);
  const [neu, setNeu] = useState(0);
  const [bearbeitet, setBearbeitet] = useState<string | null>(null);
  const fehler = m.setzen.error || m.entfernen.error || m.standard.error;
  return (
    <Abschnitt titel={`Material & Maschinen${d.materialien.length ? ` (${d.materialien.length})` : ""}`}
      rechts={null}>
      <p className="lf-hinweis">★ = Standardlieferant: in jedem Projekt holen wir es hier, solange dort nichts anderes gewählt ist.</p>
      <NeuAusBelegen firmaId={d.id} />
      {fehler && <div className="lf-fehler">{fehlerText(fehler)}</div>}
      {/* Immer offen: zuordnen ist hier die Hauptsache — tippen, antippen, nächstes. */}
      <MaterialWahl
        key={neu}
        nurBeiEingabe
        autoFokus={false}
        platzhalter="Material oder Maschine zuordnen …"
        vorhanden={d.materialien.map((x) => x.material_id)}
        onWahl={(mat) => m.setzen.mutate({ materialId: mat.id }, { onSuccess: () => setNeu((n) => n + 1) })}
      />
      {d.materialien.length === 0 && <p className="lf-leer">Noch keine Materialien hinterlegt.</p>}
      <ul className="lf-liste">
        {d.materialien.map((x) => bearbeitet === x.material_id ? (
          <li key={x.material_id}><MaterialForm lieferantId={d.id} mat={x} onFertig={() => setBearbeitet(null)} /></li>
        ) : (
          <li key={x.material_id} className="lf-zeile">
            <button type="button" className={"lf-stern" + (x.standard ? " ist-an" : "")}
              aria-pressed={x.standard} title={x.standard ? "Standardlieferant — antippen, um es zu lösen" : (x.standard_bei ? `Standard ist ${x.standard_bei} — antippen, um ${d.name} zum Standard zu machen` : "Zum Standardlieferanten machen")}
              disabled={m.standard.isPending}
              onClick={() => m.standard.mutate({ materialId: x.material_id, standard: !x.standard })}>
              {x.standard ? "★" : "☆"}
            </button>
            <div className="lf-zeile-haupt">
              {onMaterial
                ? <button type="button" className="lf-zeile-name lf-sprung" onClick={() => onMaterial(x.material_id)} title="In der Materialansicht öffnen">{x.name}{x.art === "maschine" && <em className="lf-art">Maschine</em>}</button>
                : <span className="lf-zeile-name">{x.name}{x.art === "maschine" && <em className="lf-art">Maschine</em>}</span>}
              <span className="lf-zeile-info">
                {[x.einheit, x.artikelnummer ? `Art.-Nr. ${x.artikelnummer}` : "", preisText(x),
                  !x.standard && x.standard_bei ? `Standard: ${x.standard_bei}` : "", x.notiz].filter(Boolean).join(" · ")}
              </span>
            </div>
            <div className="lf-zeile-tat">
              <button type="button" className="lf-knopf" onClick={() => setBearbeitet(x.material_id)}>Ändern</button>
            </div>
          </li>
        ))}
      </ul>
    </Abschnitt>
  );
}

/** Neue Artikel dieses Lieferanten aus seinen Belegen (Migration 182). */
function NeuAusBelegen({ firmaId }: { firmaId: string }) {
  const q = useArtikelOffen(firmaId);
  const zeilen = q.data?.zeilen ?? [];
  return zeilen.length ? <ArtikelPruefen zeilen={zeilen} /> : null;
}

const tag = (iso?: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("de-DE") : "");

/**
 * „12,40 € netto (03.09.2026) ↑ 6 %" — der jüngste Preis aus den Belegen und
 * wie er sich gegen den letzten anderen verändert hat (Benjamin: „Preis-
 * veränderungen möchte ich gern tracken").
 */
function preisText(x: LieferantMaterial): string {
  if (x.preis_netto == null) return "";
  let t = `${eur(x.preis_netto)} netto${x.preis_am ? ` (${tag(x.preis_am)})` : ""}`;
  if (x.preis_vorher != null && x.preis_vorher > 0) {
    const pct = Math.round(((x.preis_netto - x.preis_vorher) / x.preis_vorher) * 100);
    const pfeil = x.preis_netto > x.preis_vorher ? "↑" : "↓";
    t += ` ${pfeil} ${Math.abs(pct) || "<1"} % (vorher ${eur(x.preis_vorher)}${x.preis_vorher_am ? `, ${tag(x.preis_vorher_am)}` : ""})`;
  }
  return t;
}

function MaterialForm({ lieferantId, mat, onFertig }: { lieferantId: string; mat: LieferantMaterial; onFertig: () => void }) {
  const m = useLieferantMaterial(lieferantId);
  const [artikelnummer, setArtikelnummer] = useState(mat.artikelnummer);
  const [preis, setPreis] = useState(mat.preis_netto == null ? "" : String(mat.preis_netto).replace(".", ","));
  const [notiz, setNotiz] = useState(mat.notiz);
  const [loeschen, setLoeschen] = useState(false);
  const preisZahl = preis.trim() ? Number(preis.replace(/\./g, "").replace(",", ".")) : null;
  const preisOk = preisZahl === null || (Number.isFinite(preisZahl) && preisZahl >= 0);
  return (
    <div className="lf-form">
      <div className="lf-zeile-name">{mat.name}</div>
      <div className="lf-form-zwei">
        <label className="lf-label">Artikelnummer<input className="lf-feld" value={artikelnummer} onChange={(e) => setArtikelnummer(e.target.value)} /></label>
        <label className="lf-label">Preis netto{mat.einheit ? ` je ${mat.einheit}` : ""}<input className="lf-feld" inputMode="decimal" value={preis} onChange={(e) => setPreis(e.target.value)} placeholder="z. B. 12,50" /></label>
      </div>
      <label className="lf-label">Notiz<input className="lf-feld" value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Lieferzeit, Mindestmenge …" /></label>
      {!preisOk && <div className="lf-fehler">Der Preis ist keine Zahl.</div>}
      {(m.setzen.error || m.entfernen.error) && <div className="lf-fehler">{fehlerText(m.setzen.error || m.entfernen.error)}</div>}
      <div className="lf-knoepfe">
        {loeschen ? (
          <button type="button" className="lf-knopf is-gefahr" onClick={() => m.entfernen.mutate(mat.material_id, { onSuccess: onFertig })}>
            {mat.standard ? "Entfernen (ist Standard!)" : "Wirklich entfernen"}
          </button>
        ) : (
          <button type="button" className="lf-knopf is-gefahr" onClick={() => setLoeschen(true)}>Aus der Liste</button>
        )}
        <span className="lf-luecke" />
        <button type="button" className="lf-knopf" onClick={onFertig}>Abbrechen</button>
        <button type="button" className="lf-knopf is-primaer" disabled={!preisOk || m.setzen.isPending}
          onClick={() => m.setzen.mutate({ materialId: mat.material_id, artikelnummer, preis_netto: preisZahl, notiz }, { onSuccess: onFertig })}>
          {m.setzen.isPending ? "Speichert …" : "Speichern"}
        </button>
      </div>
    </div>
  );
}
